import { custom } from "devextreme/ui/dialog";
import notify from "devextreme/ui/notify";
import hideToasts from "devextreme/ui/toast/hide_toasts";
import { t } from "i18next";
import "./iti-message.scss";

export const defaultTimeout = 10000;

interface ActiveToast {
	id: string;
	position: string;
}

interface PositionGroups {
	[position: string]: ActiveToast[];
}

enum TypeMessage {
	Success = "success",
	Error = "error",
	Warning = "warning",
	Info = "info",
	Question = "question",
}

export enum Position {
	TopStart = "top left",
	TopCenter = "top center",
	TopEnd = "top right",

	CenterStart = "center left",
	Center = "center",
	CenterEnd = "center right",

	BottomStart = "bottom left",
	BottomCenter = "bottom center",
	BottomEnd = "bottom right",
}

const defaultTitle = "messages.confirm";
const activeToasts: any = [];
const toastHeights = new Map();
const spacing = 10;
const bottomMargin = 20;

function confirmButton(
	message: string,
	title = defaultTitle,
	paramsTranslateMessage?: string[],
	paramsTranslateTitle?: string[],
	keyMessageTrue?: string,
	keyMessageFalse?: string,
): Promise<boolean> {
	return new Promise<boolean>((resolve) => {
		const buttons: any = [];

		if (keyMessageFalse) {
			buttons.push({
				text: t(keyMessageFalse),
				type: "default",
				onClick: () => resolve(false),
			});
		}

		if (keyMessageTrue) {
			buttons.push({
				text: t(keyMessageTrue),
				type: "default",
				stylingMode: "contained",
				onClick: () => resolve(true),
			});
		}

		custom({
			title: t(title, { paramsTranslateTitle }),
			messageHtml: t(message, { paramsTranslateMessage }),
			buttons,
		})
			.show()
			.then((dialogResult: any) => {
				if (dialogResult === undefined) resolve(false);
			});
	});
}

function toast(
	message: string,
	type: any,
	paramsTranslateMessage?: any,
	position: string = Position.BottomCenter,
	duration: number = defaultTimeout,
	showCloseButton = true,
	showProgressBar = true,
	clearPreviousToasts = true,
) {
	if (clearPreviousToasts) {
		hideToasts();
		activeToasts.length = 0;
		toastHeights.clear();
	}

	const toastId = `toast-${Date.now()}`;
	const content = document.createElement("div");
	const progressIndicator = document.createElement("div");
	content.classList.add(toastId);

	// Crear el elemento de mensaje
	const messageElement = document.createElement("div");
	messageElement.classList.add("toast-message");
	const translatedMessage: any = t(message, paramsTranslateMessage);

	messageElement.innerHTML =
		typeof translatedMessage === "string"
			? translatedMessage
			: translatedMessage.toString();

	content.appendChild(messageElement);

	if (showCloseButton) {
		const closeButton = document.createElement("button");
		closeButton.classList.add("toast-close-btn");
		closeButton.innerHTML = "&times;";
		closeButton.onclick = (event) => {
			// Cerrar solo este toast específico
			const toastId = content.className.split(" ")[0];
			const toastElement = document.querySelector(`.${toastId}`);

			if (toastElement) {
				// Eliminar este toast del registro
				const index = activeToasts.findIndex((t: any) => t.id === toastId);
				if (index > -1) {
					activeToasts.splice(index, 1);
					toastHeights.delete(toastId);
				}
				toastElement.remove();

				updateToastPositions();
			}

			event.stopPropagation();
		};

		content.appendChild(closeButton);
	}

	if (showProgressBar) {
		const progressBar = document.createElement("div");
		progressBar.classList.add("toast-progress-bar");
		content.appendChild(progressBar);

		// Crear el indicador de progreso
		progressIndicator.classList.add("toast-progress-indicator");
		progressBar.appendChild(progressIndicator);
	}

	activeToasts.push({
		id: toastId,
		position,
	});

	const animationConfig = false;

	const object: any = {
		contentTemplate: () => content,
		type: typeof type === "object" && type.type ? type.type : type,
		displayTime: duration,
		position: {
			at: position,
			my: position,
			of: window,
			offset: "0 0", // Sin offset inicial, lo calcularemos dinámicamente
		},
		width: "auto",
		height: "auto",
		wrapperAttr: {
			class: toastId,
		},
		animation: animationConfig,
		onShown: () => {
			// Obtener la altura real del toast actual
			const currentToast = document.querySelector(
				`.${toastId} .dx-toast-content`,
			);

			if (currentToast) {
				const height = currentToast.getBoundingClientRect().height;
				toastHeights.set(toastId, height);

				// Calcular y aplicar posiciones para todos los toasts
				updateToastPositions();
			}

			// Iniciar la animación de la barra de progreso
			const startTime = Date.now();
			const timer = setInterval(() => {
				const elapsed = Date.now() - startTime;
				const progress = Math.min((elapsed / duration) * 100, 100);
				progressIndicator.style.width = `${progress}%`;

				if (progress >= 100) {
					clearInterval(timer);
				}
			}, 100);
		},
		onHidden: () => {
			// Eliminar este ID del registro cuando se oculte
			const index = activeToasts.findIndex((t: any) => t.id === toastId);
			if (index > -1) {
				activeToasts.splice(index, 1);
				toastHeights.delete(toastId);

				// Actualizar posiciones de los toasts restantes
				updateToastPositions();
			}
		},
	};

	// Si hay una clase CSS adicional, agregarla al wrapper
	if (typeof type === "object" && type.cssClass) {
		object.wrapperAttr.class += ` ${type.cssClass}`;
	}

	notify(object);
}

// Función para actualizar las posiciones de todos los toasts
function updateToastPositions(): void {
	const positionGroups: PositionGroups = {};

	// Agrupar toasts por posición
	for (const toast of activeToasts as ActiveToast[]) {
		if (!positionGroups[toast.position]) {
			positionGroups[toast.position] = [];
		}
		positionGroups[toast.position].push(toast);
	}

	// Para cada grupo de posiciones, calcular y aplicar posiciones
	for (const [position, toasts] of Object.entries(positionGroups)) {
		const isBottom = position.includes("bottom");
		const isTop = position.includes("top");

		toasts.sort((a: ActiveToast, b: ActiveToast) => {
			const idA = Number.parseInt(a.id.split("-")[1]);
			const idB = Number.parseInt(b.id.split("-")[1]);
			return idA - idB;
		});

		let accumulatedOffset = bottomMargin;

		for (const [idx, toast] of toasts.entries()) {
			const toastElement = document.querySelector(
				`.${toast.id}`,
			) as HTMLElement | null;
			if (!toastElement) continue;

			if (isBottom) {
				if (idx > 0) {
					const prevToast = toasts[idx - 1];
					accumulatedOffset += (toastHeights.get(prevToast.id) || 0) + spacing;
				}

				toastElement.style.bottom = `${accumulatedOffset}px`;
				toastElement.style.top = "auto";
			} else if (isTop) {
				if (idx > 0) {
					const prevToast = toasts[idx - 1];
					accumulatedOffset += (toastHeights.get(prevToast.id) || 0) + spacing;
				}

				toastElement.style.top = `${accumulatedOffset}px`;
				toastElement.style.bottom = "auto";
			}

			toastElement.style.position = "fixed";

			if (position.includes("left")) {
				toastElement.style.left = `${bottomMargin}px`;
				toastElement.style.right = "auto";
			} else if (position.includes("right")) {
				toastElement.style.right = `${bottomMargin}px`;
				toastElement.style.left = "auto";
			} else {
				toastElement.style.left = "50%";
				toastElement.style.transform = "translateX(-50%)";
			}
		}
	}
}

export const ItiMessageService = {
	// Popup con dos botones; Aceptar y Cancelar
	confirmYesNoButtons(
		message: string,
		title = defaultTitle,
		paramsTranslateMessage?: string[],
		paramsTranslateTitle?: string[],
	): Promise<boolean> {
		return confirmButton(
			message,
			title,
			paramsTranslateMessage,
			paramsTranslateTitle,
			"buttons.accept",
			"buttons.cancel",
		);
	},

	// Popup con dos botones; Aceptar y Cancelar
	confirmAcceptCancelButtons(
		message: string,
		title = defaultTitle,
		paramsTranslateMessage?: string[],
		paramsTranslateTitle?: string[],
	): Promise<boolean> {
		return confirmButton(
			message,
			title,
			paramsTranslateMessage,
			paramsTranslateTitle,
			"buttons.accept",
			"buttons.cancel",
		);
	},

	// Popup con dos botones personalizables
	confirmCustomButtons(
		message: string,
		title = defaultTitle,
		keyMessageTrue?: string,
		keyMessageFalse?: string,
		paramsTranslateMessage?: string[],
		paramsTranslateTitle?: string[],
	): Promise<boolean> {
		return confirmButton(
			message,
			title,
			paramsTranslateMessage,
			paramsTranslateTitle,
			keyMessageTrue,
			keyMessageFalse,
		);
	},

	// Popup con un botón personalizado
	confirmCustomButton(
		message: string,
		keyMessageTrue: string,
		title = defaultTitle,
		paramsTranslateMessage?: string[],
		paramsTranslateTitle?: string[],
	): Promise<boolean> {
		return confirmButton(
			message,
			title,
			paramsTranslateMessage,
			paramsTranslateTitle,
			keyMessageTrue,
		);
	},

	// Popup con un botón; OK
	confirmOkButton(
		message: string,
		title = defaultTitle,
		paramsTranslateMessage?: string[],
		paramsTranslateTitle?: string[],
	): Promise<any> {
		return confirmButton(
			message,
			title,
			paramsTranslateMessage,
			paramsTranslateTitle,
			"buttons.ok",
		);
	},

	// Popup con un botón; Aceptar
	confirmAcceptButton(
		message: string,
		title = defaultTitle,
		paramsTranslateMessage?: string[],
		paramsTranslateTitle?: string[],
	): Promise<boolean> {
		return confirmButton(
			message,
			title,
			paramsTranslateMessage,
			paramsTranslateTitle,
			"buttons.accept",
		);
	},

	// Popup con un botón; Cancelar
	confirmCancelButton(
		message: string,
		title = defaultTitle,
		paramsTranslateMessage?: string[],
		paramsTranslateTitle?: string[],
	): Promise<boolean> {
		return confirmButton(
			message,
			title,
			paramsTranslateMessage,
			paramsTranslateTitle,
			"buttons.cancel",
		);
	},

	// Popup con un botón; Sí
	confirmYesButton(
		message: string,
		title = defaultTitle,
		paramsTranslateMessage?: string[],
		paramsTranslateTitle?: string[],
	): Promise<boolean> {
		return confirmButton(
			message,
			title,
			paramsTranslateMessage,
			paramsTranslateTitle,
			"buttons.yes",
		);
	},

	// Popup con un botón; No
	confirmNoButton(
		message: string,
		title = defaultTitle,
		paramsTranslateMessage?: string[],
		paramsTranslateTitle?: string[],
	): Promise<boolean> {
		return confirmButton(
			message,
			title,
			paramsTranslateMessage,
			paramsTranslateTitle,
			"buttons.no",
		);
	},

	toastSuccess(
		message: string,
		paramsTranslateMessage?: string[],
		position: string = Position.BottomCenter,
		duration: number = defaultTimeout,
		showCloseButton = true,
		showProgressBar = true,
	) {
		toast(
			message,
			TypeMessage.Success,
			paramsTranslateMessage,
			position,
			duration,
			showCloseButton,
			showProgressBar,
			true, // Limpiar toasts previos
		);
	},

	toastWarning(
		message: string,
		paramsTranslateMessage?: string[],
		position: string = Position.BottomCenter,
		duration: number = defaultTimeout,
		showCloseButton = true,
		showProgressBar = true,
	) {
		toast(
			message,
			TypeMessage.Warning,
			paramsTranslateMessage,
			position,
			duration,
			showCloseButton,
			showProgressBar,
			true, // Limpiar toasts previos
		);
	},

	toastError(
		message: string,
		options?: {
			paramsTranslateMessage?: string[];
			position?: string;
			duration?: number;
			showCloseButton?: boolean;
			showProgressBar?: boolean;
			cssClass?: string;
		},
	) {
		const {
			paramsTranslateMessage,
			position = Position.BottomCenter,
			duration = defaultTimeout,
			showCloseButton = true,
			showProgressBar = true,
			cssClass,
		} = options ?? {};
		const type = cssClass
			? { type: TypeMessage.Error, cssClass }
			: TypeMessage.Error;
		toast(
			message,
			type,
			paramsTranslateMessage,
			position,
			duration,
			showCloseButton,
			showProgressBar,
			true, // Limpiar toasts previos
		);
	},

	toastInfo(
		message: string,
		paramsTranslateMessage?: string[],
		position: string = Position.BottomCenter,
		duration: number = defaultTimeout,
		showCloseButton = true,
		showProgressBar = true,
	) {
		toast(
			message,
			TypeMessage.Info,
			paramsTranslateMessage,
			position,
			duration,
			showCloseButton,
			showProgressBar,
			true, // Limpiar toasts previos
		);
	},

	// Toast con múltiples mensajes
	toastNotificationsMultiple(
		messages: { message: string; type: string; cssClass?: string }[],
		paramsTranslateMessage?: any,
		position: string = Position.BottomCenter,
		displayTime: number = defaultTimeout,
		showCloseButton = true,
		showProgressBar = true,
	) {
		hideToasts();
		activeToasts.length = 0;
		toastHeights.clear();

		const errors = messages.filter((msg) => msg.type === TypeMessage.Error);
		if (errors.length) {
			const combined = errors
				.map((msg) => String(t(msg.message, paramsTranslateMessage)))
				.join("<br>");

			toast(
				combined,
				TypeMessage.Error as any,
				undefined,
				position,
				displayTime,
				showCloseButton,
				showProgressBar,
				true, // limpia previos
			);
		}

		const others = messages.filter((msg) => msg.type !== TypeMessage.Error);
		for (const msg of others) {
			const messageType = msg.cssClass
				? { type: msg.type, cssClass: msg.cssClass }
				: msg.type;

			toast(
				msg.message,
				messageType as any,
				paramsTranslateMessage,
				position,
				displayTime,
				showCloseButton,
				showProgressBar,
				false,
			);
		}
	},

	toastHTML(message: string, type: string, paramsTranslateMessage?: any) {
		// Limpiar toasts previos
		hideToasts();

		const template = `<div data-options="dxTemplate: { name:'myContent' }">${t(
			message,
			JSON.stringify(paramsTranslateMessage),
		)}</div>`;

		const object = {
			contentTemplate: template,
			type: type,
			displayTime: defaultTimeout,
			position: Position.BottomCenter,
			animation: false,
		};

		notify(object);
	},
};
